import { useState } from 'react';
import { Movie } from '../types/movie';
import { TMDBMovie, searchMovies, getPosterUrl, getMovieDetails } from '../services/tmdbApi';
import { Plus, X, Search, Check, AlertCircle, Calendar } from 'lucide-react';

interface AddMovieFormProps {
  onAddMovie: (movie: Omit<Movie, 'id'>) => void;
  onClose: () => void;
}

export const AddMovieForm: React.FC<AddMovieFormProps> = ({ onAddMovie, onClose }) => {
  const [formData, setFormData] = useState({
    title: '',
    year: new Date().getFullYear(),
    score: 0,
    genres: [] as string[],
    director: '',
    gf: false,
    plotSummary: '',
    lastSeen: new Date().toISOString().split('T')[0] // Current date in YYYY-MM-DD format
  });

  const [newGenre, setNewGenre] = useState('');
  const [searchResults, setSearchResults] = useState<TMDBMovie[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedTmdbMovie, setSelectedTmdbMovie] = useState<TMDBMovie | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

  const validateForm = () => {
    const errors: {[key: string]: string} = {};
    
    if (formData.score === 0) {
      errors.score = 'Please select a score';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      const movieData: Omit<Movie, 'id'> = {
        ...formData,
        // Add TMDB data if available
        ...(selectedTmdbMovie && {
          tmdbId: selectedTmdbMovie.id,
          imdbId: selectedTmdbMovie.imdb_id,
          posterPath: selectedTmdbMovie.poster_path,
          backdropPath: selectedTmdbMovie.backdrop_path,
          tmdbOverview: selectedTmdbMovie.overview,
          tmdbVoteAverage: selectedTmdbMovie.vote_average,
          tmdbVoteCount: selectedTmdbMovie.vote_count,
          runtime: selectedTmdbMovie.runtime,
        })
      };
      onAddMovie(movieData);
      onClose();
    }
  };

  const searchTmdbMovies = async () => {
    if (!formData.title.trim()) return;
    
    setSearching(true);
    setSearchError(null);
    try {
      const results = await searchMovies(formData.title, formData.year);
      setSearchResults(results);
      if (results.length === 0) {
        setSearchError('No movies found with that title and year.');
      }
    } catch (error) {
      setSearchError('Failed to search TMDB. Please check your API key.');
    } finally {
      setSearching(false);
    }
  };

  const selectTmdbMovie = async (movie: TMDBMovie) => {
    setSelectedTmdbMovie(movie);
    setSearchResults([]);
    
    try {
      // Fetch detailed movie information including credits
      const detailedMovie = await getMovieDetails(movie.id);
      
      if (detailedMovie) {
        // Find the director from credits
        const director = detailedMovie.credits?.crew?.find(
          person => person.job === 'Director'
        )?.name || '';
        
        // Auto-fill all fields from TMDB data (except score)
        setFormData(prev => ({
          ...prev,
          title: detailedMovie.title,
          year: new Date(detailedMovie.release_date).getFullYear(),
          director: director,
          plotSummary: detailedMovie.overview || prev.plotSummary,
          genres: detailedMovie.genres?.map(g => g.name) || prev.genres
        }));
      } else {
        // Fallback to basic movie data
        setFormData(prev => ({
          ...prev,
          title: movie.title,
          year: new Date(movie.release_date).getFullYear(),
          plotSummary: movie.overview || prev.plotSummary,
          genres: movie.genres?.map(g => g.name) || prev.genres
        }));
      }
    } catch (error) {
      console.error('Error fetching movie details:', error);
      // Fallback to basic movie data
      setFormData(prev => ({
        ...prev,
        title: movie.title,
        year: new Date(movie.release_date).getFullYear(),
        plotSummary: movie.overview || prev.plotSummary,
        genres: movie.genres?.map(g => g.name) || prev.genres
      }));
    }
  };

  const clearTmdbSelection = () => {
    setSelectedTmdbMovie(null);
    setSearchResults([]);
    setSearchError(null);
  };

  const addGenre = () => {
    if (newGenre && !formData.genres.includes(newGenre)) {
      setFormData(prev => ({
        ...prev,
        genres: [...prev.genres, newGenre]
      }));
      setNewGenre('');
    }
  };

  const removeGenre = (genreToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      genres: prev.genres.filter(genre => genre !== genreToRemove)
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 sm:py-6 rounded-t-xl">
          <div className="flex items-center justify-between">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Add New Movie</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* TMDB Search Section */}
          <div className="bg-blue-50 rounded-xl p-3 sm:p-4 border border-blue-200">
            <h3 className="text-lg font-semibold text-blue-900 mb-3 sm:mb-4 flex items-center space-x-2">
              <Plus className="h-5 w-5" />
              <span>Search TMDB (Optional)</span>
            </h3>
            
            <div className="space-y-3 sm:space-y-0 sm:grid sm:grid-cols-1 md:grid-cols-2 sm:gap-4">
              <div>
                <label className="block text-sm font-medium text-blue-900 mb-2">
                  Movie Title
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                  placeholder="Enter movie title to search"
                />
              </div>
              
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={searchTmdbMovies}
                  disabled={searching || !formData.title.trim()}
                  className="w-full sm:w-auto btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 py-2 px-3 sm:px-4 text-sm sm:text-base"
                >
                  {searching ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                  <span>{searching ? 'Searching...' : 'Search TMDB'}</span>
                </button>
              </div>
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="space-y-2 mt-3 sm:mt-4">
                <h4 className="font-medium text-blue-900 text-sm sm:text-base">Search Results:</h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {searchResults.map((movie) => (
                    <div
                      key={movie.id}
                      className="flex items-center space-x-2 sm:space-x-3 p-2 sm:p-3 border border-blue-200 rounded-lg hover:bg-blue-100 cursor-pointer"
                      onClick={() => selectTmdbMovie(movie)}
                    >
                      {movie.poster_path ? (
                        <img
                          src={getPosterUrl(movie.poster_path, 'w92')}
                          alt={movie.title}
                          className="w-10 h-15 sm:w-12 sm:h-18 object-cover rounded"
                        />
                      ) : (
                        <div className="w-10 h-15 sm:w-12 sm:h-18 bg-blue-200 rounded flex items-center justify-center">
                          <span className="text-xs text-blue-500">No poster</span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-blue-900 text-sm sm:text-base truncate">{movie.title}</div>
                        <div className="text-xs sm:text-sm text-blue-700">
                          {new Date(movie.release_date).getFullYear()} • {movie.vote_average.toFixed(1)}/10
                        </div>
                      </div>
                      <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 flex-shrink-0" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Error Message */}
            {searchError && (
              <div className="flex items-center space-x-2 text-red-600 text-sm mt-3">
                <AlertCircle className="h-4 w-4" />
                <span>{searchError}</span>
              </div>
            )}

            {/* Selected Movie */}
            {selectedTmdbMovie && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-3 sm:mt-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                    <span className="font-medium text-green-800 text-sm sm:text-base">
                      Selected: {selectedTmdbMovie.title} ({new Date(selectedTmdbMovie.release_date).getFullYear()})
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={clearTmdbSelection}
                    className="text-green-600 hover:text-green-800 text-sm self-start sm:self-auto"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Manual Entry Section */}
          <div className="space-y-4 sm:space-y-6">
            {/* Current Date Indicator */}
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Calendar className="h-4 w-4" />
                <span>Watched on: <span className="font-medium text-gray-900">{formData.lastSeen}</span></span>
              </div>
            </div>
            
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm sm:text-base"
                placeholder="Movie title"
              />
            </div>

            {/* Year, Score, and Director in a responsive grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {/* Year */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Year
                </label>
                <input
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData(prev => ({ ...prev, year: parseInt(e.target.value) || 0 }))}
                  className="w-full px-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm sm:text-base"
                  min="1900"
                  max={new Date().getFullYear() + 1}
                />
              </div>

              {/* Score */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Score *
                </label>
                <input
                  type="number"
                  min="0"
                  max="10"
                  step="0.1"
                  value={formData.score}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, score: parseFloat(e.target.value) || 0 }));
                    if (validationErrors.score) {
                      setValidationErrors(prev => ({ ...prev, score: '' }));
                    }
                  }}
                  className={`w-full px-3 py-2.5 sm:py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm sm:text-base ${
                    validationErrors.score ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="0-10"
                />
                {validationErrors.score && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {validationErrors.score}
                  </p>
                )}
              </div>

              {/* GF Checkbox */}
              <div className="flex items-end">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.gf}
                    onChange={(e) => setFormData(prev => ({ ...prev, gf: e.target.checked }))}
                    className="h-4 w-4 accent-primary-600 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Watched with GF</span>
                </label>
              </div>

              {/* Director */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Director *
                </label>
                <input
                  type="text"
                  required
                  value={formData.director}
                  onChange={(e) => setFormData(prev => ({ ...prev, director: e.target.value }))}
                  className="w-full px-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm sm:text-base"
                  placeholder="Director name"
                />
              </div>
            </div>

            {/* Genres */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Genres
              </label>
              <div className="flex flex-wrap gap-2 mb-3">
                {formData.genres.map((genre, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-primary-100 text-primary-800"
                  >
                    {genre}
                    <button
                      type="button"
                      onClick={() => removeGenre(genre)}
                      className="ml-1 sm:ml-2 text-primary-600 hover:text-primary-800"
                    >
                      <X className="h-3 w-3 sm:h-4 sm:w-4" />
                    </button>
                  </span>
                ))}
              </div>
              
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                <input
                  type="text"
                  value={newGenre}
                  onChange={(e) => setNewGenre(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addGenre()}
                  className="flex-1 px-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm sm:text-base"
                  placeholder="Add a genre"
                />
                <button
                  type="button"
                  onClick={addGenre}
                  disabled={!newGenre.trim()}
                  className="px-3 py-2.5 sm:py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                >
                  Add
                </button>
              </div>
            </div>

            {/* Plot Summary */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Plot Summary
              </label>
              <textarea
                value={formData.plotSummary}
                onChange={(e) => setFormData(prev => ({ ...prev, plotSummary: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm sm:text-base"
                placeholder="Brief plot summary (optional)"
              />
            </div>

            {/* Last Seen Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last Seen Date
              </label>
              <input
                type="date"
                value={formData.lastSeen}
                onChange={(e) => setFormData(prev => ({ ...prev, lastSeen: e.target.value }))}
                className="w-full px-3 py-2.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm sm:text-base"
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 pt-4 sm:pt-6 border-t border-gray-200">
            <button
              type="submit"
              className="flex-1 sm:flex-none btn-primary py-2.5 sm:py-2 px-4 text-sm sm:text-base"
            >
              Add Movie
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 sm:flex-none btn-secondary py-2.5 sm:py-2 px-4 text-sm sm:text-base"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
